import React, { useState, useRef, useEffect } from 'react';
import { api } from '@/lib/api';
import { Robot, X, Spinner, PaperPlaneTilt } from '@phosphor-icons/react';

export const CaseChatPanel = ({ caseId, messages, setMessages, onClose }) => {
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const msg = chatInput;
    setChatInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, created_at: new Date().toISOString() }]);
    setChatLoading(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/chat`, { message: msg });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, created_at: new Date().toISOString() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, an error occurred. Please try again.', created_at: new Date().toISOString() }]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white border-l border-[#E5E7EB] z-50 flex flex-col shadow-xl" data-testid="chat-panel">
      <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-2">
          <Robot size={20} className="text-[#002FA7]" />
          <span className="font-outfit font-semibold text-[#09090B]">Case AI Assistant</span>
        </div>
        <button onClick={onClose} data-testid="close-chat-button" className="p-1 hover:bg-[#F3F4F6] rounded transition-colors">
          <X size={18} className="text-[#6B7280]" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8 text-[#6B7280] font-ibm text-sm">
            <Robot size={40} className="mx-auto mb-3 text-[#E5E7EB]" />
            Ask questions about your case. The AI will reference your uploaded documents and analysis.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m._id || `${m.role}-${m.created_at || i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3.5 py-2.5 rounded-lg text-sm font-ibm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#002FA7] text-white' : 'bg-[#F3F4F6] text-[#09090B]'}`}
              data-testid={`chat-msg-${m.role}-${i}`}>
              {m.content}
            </div>
          </div>
        ))}
        {chatLoading && <div className="flex justify-start"><div className="px-3.5 py-2.5 rounded-lg bg-[#F3F4F6] text-sm font-ibm text-[#6B7280]"><Spinner size={16} className="animate-spin inline mr-1" /> Thinking...</div></div>}
        <div ref={chatEndRef} />
      </div>
      <form onSubmit={sendChat} className="p-4 border-t border-[#E5E7EB] flex gap-2">
        <input data-testid="chat-input" type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Ask about your case..."
          className="flex-1 px-3.5 py-2.5 border border-[#E5E7EB] rounded-md text-sm font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        <button data-testid="chat-send-button" type="submit" disabled={chatLoading || !chatInput.trim()}
          className="p-2.5 bg-[#002FA7] text-white rounded-md hover:bg-[#00237D] disabled:opacity-50 transition-all">
          <PaperPlaneTilt size={18} weight="fill" />
        </button>
      </form>
    </div>
  );
};
